import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UserIcon, ArrowRightIcon, ShieldCheckIcon } from 'lucide-react';
interface WelcomeScreenProps {
  onStart: (firstName: string, lastName: string) => void;
}
export function WelcomeScreen({
  onStart
}: WelcomeScreenProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setError(true);
      return;
    }
    onStart(firstName.trim(), lastName.trim());
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen flex flex-col md:flex-row w-full bg-white" data-id="element-338">
      {/* Left Side - Branding */}
      <div className="md:w-1/2 bg-gradient-to-br from-[#8DC63F] to-[#6da82e] p-8 md:p-16 flex flex-col justify-center items-center text-center relative overflow-hidden" data-id="element-339">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none" data-id="element-340">
          <ShieldCheckIcon className="absolute -top-20 -left-20 w-96 h-96 text-white transform -rotate-12" data-id="element-341" />
          <ShieldCheckIcon className="absolute -bottom-20 -right-20 w-96 h-96 text-white transform rotate-12" data-id="element-342" />
        </div>

        <div className="relative z-10 flex flex-col items-center" data-id="element-343">
          <div className="bg-white p-6 rounded-2xl shadow-xl mb-12" data-id="element-344">
            <img src="https://cdn.magicpatterns.com/uploads/8ecn9exgjkQqt4cTmAm7H4/Logo.png" alt="INTIGOO Logo" className="h-16 md:h-20 object-contain" data-id="element-345" />
          </div>

          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight" data-id="element-346">
            Mitarbeiter-Schulung
          </h1>
          <p className="text-green-50 text-lg md:text-xl max-w-md font-medium" data-id="element-347">
            Arbeitsunterweisungen & Sicherheitsrichtlinien
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="md:w-1/2 flex items-center justify-center p-8 md:p-16 bg-[#f8f9fa]" data-id="element-348">
        <div className="w-full max-w-md" data-id="element-349">
          <div className="mb-10" data-id="element-350">
            <h2 className="text-3xl font-bold text-[#1a1a2e] mb-3" data-id="element-351">
              Willkommen
            </h2>
            <p className="text-[#4a4a5a] text-lg" data-id="element-352">
              Bitte geben Sie Ihren Namen ein, um die Schulung zu starten.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-353">
            <div data-id="element-354">
              <label htmlFor="firstName" className="block text-sm font-semibold text-[#1a1a2e] mb-2" data-id="element-355">
                Vorname
              </label>
              <div className="relative" data-id="element-356">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-357">
                  <UserIcon className="h-5 w-5 text-gray-400" data-id="element-358" />
                </div>
                <input type="text" id="firstName" value={firstName} onChange={e => {
                setFirstName(e.target.value);
                setError(false);
              }} className={`block w-full pl-12 pr-4 py-4 border ${error && !firstName.trim() ? 'border-red-500 ring-1 ring-red-500 bg-red-50' : 'border-gray-200 bg-white'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8DC63F] focus:border-transparent transition-all text-lg shadow-sm`} placeholder="Max" data-id="element-359" />
              </div>
            </div>

            <div data-id="element-360">
              <label htmlFor="lastName" className="block text-sm font-semibold text-[#1a1a2e] mb-2" data-id="element-361">
                Nachname
              </label>
              <div className="relative" data-id="element-362">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-363">
                  <UserIcon className="h-5 w-5 text-gray-400" data-id="element-364" />
                </div>
                <input type="text" id="lastName" value={lastName} onChange={e => {
                setLastName(e.target.value);
                setError(false);
              }} className={`block w-full pl-12 pr-4 py-4 border ${error && !lastName.trim() ? 'border-red-500 ring-1 ring-red-500 bg-red-50' : 'border-gray-200 bg-white'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8DC63F] focus:border-transparent transition-all text-lg shadow-sm`} placeholder="Mustermann" data-id="element-365" />
              </div>
            </div>

            {error && <motion.p initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-red-500 text-sm font-medium" data-id="element-366">
                Bitte füllen Sie beide Felder aus.
              </motion.p>}

            <button type="submit" className="w-full flex items-center justify-center py-4 px-6 border border-transparent rounded-xl shadow-md text-white bg-[#8DC63F] hover:bg-[#7ab036] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#8DC63F] transition-all font-bold text-lg mt-8 group" data-id="element-367">
              Schulung starten
              <ArrowRightIcon className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform" data-id="element-368" />
            </button>
          </form>
        </div>
      </div>
    </motion.div>;
}